import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import { maskCNPJ, isValidCNPJ } from '../utils/masks'; // #231 — máscara + validação CNPJ
import Button from './ui/Button';
import './Login.css';

/**
 * #79 — Cadastro público self-service.
 *
 * Reaproveita a régua visual da tela de Login (Login.css). Cria a conta via
 * authService.signup → o backend cria tenant + 1º admin + aceite de termos e
 * devolve o par de tokens; o auto-login deixa o usuário direto no app (onde o
 * micro-tour #239 aparece na primeira sessão).
 *
 * Decisões v1: sem verificação de e-mail. CNPJ OBRIGATÓRIO (#314 — sustenta o
 * dedup do arrependimento de 7 dias e da degustação grátis; deterrente
 * anti-uso-de-CNPJ-de-terceiro, já que ele aparece nos relatórios).
 *
 * #169 — código de indicação de consultor (opcional). Vai no payload como
 * codigo_indicacao; o backend casa com um consultor ativo e atribui a comissão.
 * Inválido é ignorado silenciosamente — nunca barra o cadastro.
 */
function Signup({ onSignupSuccess }) {
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [codigoIndicacao, setCodigoIndicacao] = useState('');
  const [aceitoTermos, setAceitoTermos] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // #169 — pré-preenche o código de indicação a partir do LINK (?ref=QS-XXXX),
  // pra o consultor compartilhar um link em vez de pedir pra digitar o cupom. O
  // campo segue editável; ausente/vazio não muda nada. Aceita ?ref e o alias
  // ?codigo_indicacao. O backend revalida e ignora código inválido — aqui é só UX.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || params.get('codigo_indicacao') || '').trim();
      if (ref) setCodigoIndicacao(ref.toUpperCase());
    } catch (e) { /* sem window (edge) → ignora */ }
  }, []);

  const senhaCurta = senha.length > 0 && senha.length < 8;
  const cnpjVazio = cnpj.trim().length === 0;
  const cnpjInvalido = cnpj.trim().length > 0 && !isValidCNPJ(cnpj);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!aceitoTermos) {
      setError('É necessário aceitar os termos de uso para criar a conta.');
      return;
    }
    if (senhaCurta) {
      setError('A senha precisa ter ao menos 8 caracteres.');
      return;
    }
    if (cnpjVazio) {
      setError('Informe o CNPJ da empresa.');
      return;
    }
    if (cnpjInvalido) {
      setError('CNPJ inválido — confira os números.');
      return;
    }

    setLoading(true);
    try {
      const user = await authService.signup({
        nomeEmpresa: nomeEmpresa.trim(),
        nome: nome.trim(),
        email: email.trim(),
        senha,
        cnpj: cnpj.trim(),
        aceitoTermos,
        codigoIndicacao: codigoIndicacao.trim(),
      });
      onSignupSuccess(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-grid-container">
        {/* Left Column - Hero Section (reaproveita o layout do Login) */}
        <div className="login-hero-column">
          <div className="hero-card-main">
            <div className="hero-content">
              <img src="/qaasa-texto-dark.png" alt="Qaa.sa Logo" className="hero-logo" />
              <h1 className="hero-title">
                Comece em minutos,<br />
                <span className="hero-title-accent">sem instalar nada</span>
              </h1>
              <p className="hero-description">
                Crie sua conta e registre a primeira não-conformidade hoje. A IA cuida da análise de causa raiz.
              </p>
            </div>
            <div className="hero-decoration"></div>
          </div>

          <div className="feature-card">
            <span className="material-symbols-outlined feature-icon">bolt</span>
            <div>
              <h3 className="feature-title">Sem cartão, sem compromisso</h3>
              <p className="feature-description">Cadastre-se, explore e registre suas NCs. Você decide quando evoluir de plano.</p>
            </div>
          </div>

          <div className="feature-card-primary">
            <div className="feature-primary-content">
              <h3 className="feature-primary-title">Da planilha para a inteligência.</h3>
              <p className="feature-primary-description">Registro, workflow, eficácia e recorrência — a gestão de não-conformidades inteira, guiada pelo método ISO.</p>
            </div>
            <div className="feature-primary-icon">
              <span className="material-symbols-outlined">workspace_premium</span>
            </div>
          </div>
        </div>

        {/* Right Column - Signup Form */}
        <div className="login-form-column">
          <div className="login-form-header">
            <h2 className="form-title">Criar conta</h2>
            <p className="form-subtitle">Preencha os dados da sua empresa para começar a usar a Qaa.sa.</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form-main">
            {/* Nome da empresa */}
            <div className="form-group">
              <label className="form-label">Nome da empresa</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">apartment</span>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Sua empresa LTDA"
                  value={nomeEmpresa}
                  onChange={(e) => setNomeEmpresa(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Seu nome */}
            <div className="form-group">
              <label className="form-label">Seu nome</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">person</span>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Nome e sobrenome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* E-mail */}
            <div className="form-group">
              <label className="form-label">E-mail Corporativo</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">mail</span>
                <input
                  type="email"
                  className="form-input"
                  placeholder="nome@empresa.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Senha */}
            <div className="form-group">
              <label className="form-label">Senha</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">lock</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Mínimo 8 caracteres"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="visibility-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {senhaCurta && (
                <p style={{ fontSize: '12px', color: '#dc2626', margin: '6px 2px 0' }}>
                  A senha precisa ter ao menos 8 caracteres.
                </p>
              )}
            </div>

            {/* CNPJ (obrigatório — #314) */}
            <div className="form-group">
              <label className="form-label">
                CNPJ <span style={{ color: '#dc2626', fontWeight: 400 }}>*</span>
              </label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">badge</span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(e) => setCnpj(maskCNPJ(e.target.value))}
                  required
                  disabled={loading}
                />
              </div>
              {cnpjInvalido && (
                <p style={{ fontSize: '12px', color: '#dc2626', margin: '6px 2px 0' }}>
                  CNPJ inválido — confira os números.
                </p>
              )}
            </div>

            {/* Código de indicação (opcional) — #169 consultor indicador */}
            <div className="form-group">
              <label className="form-label">
                Código de indicação <span style={{ color: '#94a3b8', fontWeight: 400 }}>(opcional)</span>
              </label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">handshake</span>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex.: QS-A2BC"
                  value={codigoIndicacao}
                  onChange={(e) => setCodigoIndicacao(e.target.value.toUpperCase())}
                  disabled={loading}
                />
              </div>
              <p style={{ fontSize: '12px', color: '#94a3b8', margin: '6px 2px 0' }}>
                Tem um código de um consultor parceiro? Informe aqui.
              </p>
            </div>

            {/* Aceite de termos */}
            <div className="remember-wrapper">
              <input
                type="checkbox"
                id="aceito-termos"
                className="remember-checkbox"
                checked={aceitoTermos}
                onChange={(e) => setAceitoTermos(e.target.checked)}
              />
              <label htmlFor="aceito-termos" className="remember-label">
                Li e aceito os{' '}
                <a href="/termos" target="_blank" rel="noreferrer" className="forgot-link">Termos de Uso</a>{' '}
                e a{' '}
                <a href="/privacidade" target="_blank" rel="noreferrer" className="forgot-link">Política de Privacidade</a>.
              </label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <Button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'CRIANDO CONTA...' : 'CRIAR CONTA'}
              <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>arrow_forward</span>
            </Button>
          </form>

          <div className="sso-section">
            <div className="sso-content">
              <p className="version-text">
                Já tem conta? <a href="/" className="forgot-link">Entrar</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
